/**
 * English fallbacks. `useRedeemT()` prefers the CMS value and lands here rather than showing a key.
 * Namespace `Redeem Gift` = `l_config_language.page_id 44`, also rendered by the legacy PHP page.
 * The English can go once the CMS carries every key; the KEY LIST must stay — it is the source of
 * `RedeemMessageKey`, which type-checks every `t()` call.
 */

export const REDEEM_NS = 'Redeem Gift'

export const redeemFallbackEn = {
  // ═══ GROUP 1 — row exists; CMS wins in production. Keep in sync, do not improve. ═══

  // The giver, not the recipient — LIKE-matched against g_gift.receive_from.
  LABEL_INPUT_GIFT_FROM: 'GIFT FROM',
  LABEL_INPUT_GIFT_NUMBER: 'eGift ID',
  TITLE_REDEEM_GIFT: 'REDEEM GIFT CERTIFICATE',
  TITLE_SEARCH_GIFT: 'Lucky you, you got an eGift!',
  BUTTON_SEARCH: 'Search',
  LABEL_CAPTCHA: 'Verify Captcha',
  ERROR_INPUT_REQUIRED: 'Please input information (*)',
  ERROR_CAPTCHA_REQUIRED: 'Please verify you are not robot (*)',
  ERROR_INPUT_EMAIL_FORMAT: 'Please enter a valid email address.',
  ERROR_INPUT_PHONE_FORMAT: 'Please enter a valid phone. e.g. +66xxxxxxxxx',

  // Booking
  TITLE_BOOKING_INFO: 'Booking Information',
  DESCRIPTION_BOOKING_INFO:
    'You’re only a few minutes away from your spa appointment! Please enter the booking information to make an appointment such as your desired branch, the time, and number of guests.',
  LABEL_INPUT_LOCATION: 'LOCATION',
  LABEL_INPUT_DATE: 'DATE',
  LABEL_INPUT_TIME: 'TIME',
  LABEL_INPUT_GUEST: 'GUEST',
  TITLE_CUSTOMER_INFO: 'Customer Information',
  LABEL_INPUT_YOUR_NAME: 'NAME',
  LABEL_INPUT_YOUR_EMAIL: 'EMAIL',
  BUTTON_SUBMIT: 'Confirm',

  // Confirmation + gift card
  LABEL_INPUT_GIFT_EXPIRED: 'Gift Expired Date',
  LABEL_BOOKING_NUMBER: 'Booking ID',
  LABEL_LOCATION: 'Location',

  // ═══ GROUP 2 — no row yet. This English ships in all 7 locales today. ═══

  // Also used by both payment forms, which have no row on any page either.
  ERROR_INPUT_EMAIL_REQUIRED: 'Please enter your email address.',

  // Hero. Line 1 is the CMS TITLE_SEARCH_GIFT; this is the orange second line.
  TITLE_HERO_ACCENT: 'Redeem it online in one page.',
  // Not DESCRIPTION_SEARCH_GIFT: 366 chars (547 th), raw URL, hardcoded phone, addresses the buyer.
  DESCRIPTION_HERO:
    'Enter your eGift details, choose a spa, date and time — your booking is confirmed right away.',
  // Laravel matches gift_number EXACTLY, so the sample must not teach separators.
  LABEL_INPUT_GIFT_FROM_PLACEHOLDER: 'Name of the person who sent it to you',
  LABEL_INPUT_GIFT_NUMBER_PLACEHOLDER: 'e.g. GC12345',
  BUTTON_VERIFIED: 'Verified',
  // Asks the recipient, not the buyer — only recipients open this page.
  DESCRIPTION_REDEEM_GAYA: 'Is your eGift from Gaaya Spa?',
  BUTTON_REDEEM_GAYA: 'Redeem at Gaaya',

  // Captcha. LABEL_CAPTCHA is the modal heading; the CMS row describes a checkbox, not a slider.
  LABEL_CAPTCHA_SLIDE: 'Slide the piece into place',
  ERROR_CAPTCHA_FAILED: 'Not quite — try again',

  // Gift card
  LABEL_YOUR_EGIFT: 'Your eGift',
  LABEL_GIFT_META: '#{number} · from {sender}',
  LABEL_GIFT_EXPIRES_IN_DAYS: '{days, plural, one {# day left} other {# days left}}',
  LABEL_GIFT_EXPIRES_TODAY: 'Last day to redeem',

  // Booking form — the CMS rows for these slots are a different role or a shouting label
  LABEL_INPUT_TREATMENT_HOURS: 'Treatment hours',
  LABEL_INPUT_MOBILE: 'Mobile',
  LABEL_INPUT_REMARK_OPTIONAL: 'Anything we should know? (optional)',
  DESCRIPTION_CUSTOMER_INFO_EMAIL: 'We’ll send your booking confirmation to this email.',
  LABEL_GUEST_COUNT: '{count, plural, one {# guest} other {# guests}}',
  LABEL_INPUT_SELECT_PLACEHOLDER: 'Select',
  NOTICE_PICK_BRANCH_FIRST: 'Choose a location and date to see available times.',
  // No booking data client-side — an empty list means outside hours or too late today, never taken.
  NOTICE_NO_TIME_AVAILABLE:
    'No bookable times for this date at this location — same-day bookings need at least 2 hours’ notice. Try a later date or another spa.',

  // Sticky summary bar
  LABEL_BOOKING_SUMMARY: 'Booking summary',
  NOTICE_SUMMARY_INCOMPLETE: 'Fill in the details above to continue',
  BUTTON_BOOKING_LOADING: 'Booking…',
  BUTTON_HIDE_SUMMARY: 'Hide summary',

  // Confirmation
  TITLE_CONGRATS_DATE: 'See you on {date}',
  // The mail POST is fire-and-forget, so this cannot claim the mail was sent.
  DESCRIPTION_CONGRATS_EMAIL:
    'We’re sending a confirmation to {email}. If it hasn’t arrived in a few minutes, check your spam folder or contact us with the booking ID above.',
  DESCRIPTION_CONGRATS_NO_EMAIL: 'Your booking is confirmed.',
  LABEL_DATE_TIME: 'Date & time',
  LABEL_DURATION: 'Duration',
  TITLE_WHAT_TO_BRING: 'What to bring:',
  // An eGift arrives by email — there is no original certificate to bring.
  DESCRIPTION_WHAT_TO_BRING:
    'Your booking ID above and your eGift — show it on your phone or bring a printout. Anything not covered by your eGift will be billed at the spa.',
  BUTTON_ADD_CALENDAR: 'Add to calendar',
  BUTTON_BROWSE_PROMOTIONS: 'Browse promotions',
  LABEL_GIFT_NUMBER: 'eGift ID',
  LABEL_CUSTOMER_NAME: 'Name',
  LABEL_CUSTOMER_PHONE: 'Phone',
  BUTTON_START_OVER: 'Redeem another eGift',
  BUTTON_CHANGE_GIFT: 'Use a different eGift',

  // Errors. New body keys: the CMS ERROR_ALERT_GIFT_* rows are swal bodies, so under a separate
  // title they repeat it, and NOTFOUND's ": Click Here" tail becomes dead text.
  ERROR_TITLE_GIFT_USED: 'This eGift has already been used',
  ERROR_TITLE_GIFT_NOTFOUND: 'We could not find this eGift',
  ERROR_TITLE_GIFT_EXPIRED: 'This eGift has expired',
  ERROR_TITLE_GIFT_VOID: 'This eGift is no longer valid',
  ERROR_BODY_GIFT_USED:
    'It is linked to the booking below. If you think this is a mistake, contact us and we will check it for you.',
  ERROR_BODY_GIFT_NOTFOUND:
    'Check that the sender’s name and eGift ID match what is printed on the certificate, or reach out and we will help you sort it out.',
  ERROR_BODY_GIFT_EXPIRED:
    'It passed its expiry date and can no longer be redeemed. Contact us to talk through your options.',
  ERROR_BODY_GIFT_VOID: 'Contact our team and we will look into it for you.',

  ERROR_TITLE_NETWORK: 'Something went wrong',
  ERROR_ALERT_NETWORK:
    'We could not reach the booking system. Nothing was booked — please try again in a moment.',
  // Separate from transport failure: InsertBooking rolls back manually, so a server error can
  // leave the gift used and a retry would burn it.
  ERROR_TITLE_BOOKING_UNCONFIRMED: 'We could not confirm your booking',
  ERROR_BODY_BOOKING_UNCONFIRMED:
    'Please do not book again — contact us with your eGift ID and we will check whether it went through.',
  ERROR_TITLE_RATE_LIMITED: 'Too many attempts',
  ERROR_ALERT_RATE_LIMITED: 'Please wait a minute before trying again.',
  ERROR_TITLE_CAPTCHA: 'Puzzle not solved',
  ERROR_ALERT_GIFT_USED_DETAIL: 'Booked as {ref} on {date} at {branch}.',
  // Says LINE because the button opens LINE pre-filled, not the contact page. Matches the CMS row
  // staged in plan/sql/redeem-l_config_language.sql.
  BUTTON_CONTACT_LINE: 'Contact us on LINE',

  // Recommend. create_status:false also fires from the gc_per_day cap, so no timing-specific wording.
  TITLE_RECOMMEND: 'We can’t book that exact time',
  DESCRIPTION_RECOMMEND: 'Your eGift is still valid — here is the closest alternative we found.',
  // Heading for the flattened branch+time button grid — each button already names its own
  // branch, so this no longer needs a per-branch "different location" variant.
  LABEL_RECOMMEND_NEARBY_BRANCHES: 'Suggested nearby branches',
  NOTICE_CHECKING_AVAILABILITY: 'Checking availability…',

  // Reason-specific headings. TITLE_RECOMMEND says "that exact time" — wrong when the problem is
  // the date or the notice period.
  TITLE_RECOMMEND_ADVANCE: 'That’s a little too soon',
  DESCRIPTION_RECOMMEND_ADVANCE:
    'Same-day bookings need at least 2 hours’ notice so the spa can prepare your room.',
  TITLE_RECOMMEND_PAST: 'That time has already passed',
  DESCRIPTION_RECOMMEND_PAST: 'Please choose a later time, or another date.',
  // Names the eGift limit, not "fully booked": the cap is checked before rooms are, so this path
  // never learns whether the spa has space. Claiming it is full would be wrong, and would talk a
  // customer out of a paid booking it might still take.
  // Same wording as LABEL_BRANCH_GIFT_FULL on purpose — a visitor can meet both on one screen, and
  // matching words are what tell them it is the same thing being talked about.
  TITLE_RECOMMEND_GIFT_CAP: 'This spa has reached its eGift limit for that date',
  DESCRIPTION_RECOMMEND_GIFT_CAP:
    'Each spa accepts a limited number of eGift redemptions per day, and this one has used up its allowance. Your eGift is still valid — try another date or location.',
  TITLE_RECOMMEND_OUTSIDE_HOURS: 'The spa is closed by then',

  DESCRIPTION_RECOMMEND_OUTSIDE_HOURS:
    'Your treatment would run past closing time at this location.',
  NOTICE_RECOMMEND_NONE:
    'We couldn’t find an available time for this date. Please try another date or location, or call us at 02-262-2122 and we’ll arrange a time for you.',

  // Branches already at their daily eGift limit, marked in the dropdown once a date is chosen.
  // A suffix to the branch name, so it carries no punctuation or capital of its own.
  //
  // This label has to explain itself: the branch is disabled, so its visitor never reaches the
  // suggestion panel that would have said more. Hence "limit", not "slots" — in a booking context
  // a slot reads as a TIME slot, and the spa may well have plenty of those free to a paying guest.
  LABEL_BRANCH_GIFT_FULL: 'eGift limit reached',

  // Session
  NOTICE_SESSION_EXPIRED:
    'Your session timed out after 60 minutes. Please look up your eGift again.',

  // Validation
  ERROR_INPUT_DATE_PAST: 'Please choose today or a later date.',
  ERROR_INPUT_DATE_AFTER_EXPIRY: 'This is after your eGift expires.',
  ERROR_INPUT_DATE_INVALID: 'Please choose a valid date.',
  ERROR_INPUT_TOO_LONG: 'This is too long.'
} as const

export type RedeemMessageKey = keyof typeof redeemFallbackEn

/** Keys that already have a `page_id 44` row. The SQL generator must not create these. */
export const CMS_BACKED_KEYS = [
  'LABEL_INPUT_GIFT_FROM',
  'LABEL_INPUT_GIFT_NUMBER',
  'TITLE_REDEEM_GIFT',
  'TITLE_SEARCH_GIFT',
  'BUTTON_SEARCH',
  'LABEL_CAPTCHA',
  'ERROR_INPUT_REQUIRED',
  'ERROR_CAPTCHA_REQUIRED',
  'ERROR_INPUT_EMAIL_FORMAT',
  'ERROR_INPUT_PHONE_FORMAT',
  'TITLE_BOOKING_INFO',
  'DESCRIPTION_BOOKING_INFO',
  'LABEL_INPUT_LOCATION',
  'LABEL_INPUT_DATE',
  'LABEL_INPUT_TIME',
  'LABEL_INPUT_GUEST',
  'TITLE_CUSTOMER_INFO',
  'LABEL_INPUT_YOUR_NAME',
  'LABEL_INPUT_YOUR_EMAIL',
  'BUTTON_SUBMIT',
  'LABEL_INPUT_GIFT_EXPIRED',
  'LABEL_BOOKING_NUMBER',
  'LABEL_LOCATION'
] as const satisfies readonly RedeemMessageKey[]
